import React, { useState, useEffect } from 'react';
import { User } from '../../types';
import { Search, Plus, Pencil, Loader2, AlertTriangle, Users, Settings, Database, ChevronRight, CheckCircle, X, Trash2 } from 'lucide-react';
import { fetchLookups, fetchUsers, fetchModules, createLookup, updateLookup, createUser, updateUser } from '../../services/apiService';

const StatusBadge = ({ status, label }: { status: any, label: string }) => {
    const colors: any = { 
        'ACTV': 'text-green-400 bg-green-900/30', 
        'SUSP': 'text-red-400 bg-red-900/30', 
        'PEND': 'text-yellow-400 bg-yellow-900/30'
    };
    return <span className={`${colors[status] || 'text-white bg-slate-700'} px-2 py-1 rounded text-xs font-bold`}>{label}</span>;
}

const Modal = ({ title, onClose, children, size = 'lg' }: { title: string, onClose: () => void, children: React.ReactNode, size?: 'sm' | 'md' | 'lg' | 'xl' }) => (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-fade-in">
        <div className={`bg-slate-800 rounded-lg border border-slate-700 w-full shadow-2xl flex flex-col max-h-[95vh] ${size === 'xl' ? 'max-w-6xl' : size === 'lg' ? 'max-w-4xl' : size === 'md' ? 'max-w-2xl' : 'max-w-md'}`}>
            <div className="p-4 border-b border-slate-700 flex justify-between items-center bg-slate-900 rounded-t-lg">
                <h3 className="text-xl font-bold text-white">{title}</h3>
                <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5"/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
                {children}
            </div>
        </div>
    </div>
);

export const AdminUsersView: React.FC = () => {
    const [tab, setTab] = useState<'Users' | 'Config'>('Users');
    
    // User State
    const [users, setUsers] = useState<User[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Config State
    const [modules, setModules] = useState<any[]>([]);
    const [selectedModule, setSelectedModule] = useState<any | null>(null);
    const [currentLookups, setCurrentLookups] = useState<Record<string, any[]>>({});
    
    // Modal State
    const [showLookupModal, setShowLookupModal] = useState(false);
    const [editingLookup, setEditingLookup] = useState<{ name: string, options: any[] } | null>(null);
    const [showUserModal, setShowUserModal] = useState(false);
    const [editingUser, setEditingUser] = useState<Partial<User> & { password?: string } | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [coreLookups, setCoreLookups] = useState<Record<string, any[]>>({});

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setLoading(true);
                const [userData, moduleData, coreLookupData] = await Promise.all([
                    fetchUsers(),
                    fetchModules(),
                    fetchLookups('CORE')
                ]);
                setUsers(userData);
                setModules(moduleData);
                setCoreLookups(coreLookupData);
                
                if (moduleData.length > 0) {
                    setSelectedModule(moduleData[0]);
                }
            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        loadInitialData();
    }, []);

    useEffect(() => {
        const loadModuleLookups = async () => {
            if (!selectedModule) return;
            try {
                const data = await fetchLookups(selectedModule.code);
                setCurrentLookups(data);
            } catch (err: any) {
                console.error("Failed to fetch lookups for module", err);
            }
        };
        if (tab === 'Config') loadModuleLookups();
    }, [selectedModule, tab]);

    const getCoreLabel = (lookupName: string, code: any) => {
        const options = coreLookups[lookupName] || [];
        const found = options.find((o: any) => String(o.code) === String(code));
        return found ? found.label : code;
    };

    const handleSaveLookup = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedModule || !editingLookup) return;

        setIsSaving(true);
        try {
            const exists = Object.keys(currentLookups).includes(editingLookup.name);
            if (exists) {
                await updateLookup(selectedModule.code, editingLookup.name, editingLookup.options);
            } else {
                await createLookup(selectedModule.code, editingLookup.name, editingLookup.options);
            }
            
            // Refresh data
            const data = await fetchLookups(selectedModule.code);
            setCurrentLookups(data);
            setShowLookupModal(false);
            setEditingLookup(null);
        } catch (err: any) {
            alert(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingUser) return;

        setIsSaving(true);
        try {
            if (editingUser.id) {
                await updateUser(editingUser.id, editingUser);
            } else {
                await createUser(editingUser);
            }
            
            // Refresh data
            const userData = await fetchUsers();
            setUsers(userData);
            setShowUserModal(false);
            setEditingUser(null);
        } catch (err: any) {
            alert(err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleAddOption = () => {
        if (!editingLookup) return;
        const newOptions = [...editingLookup.options, { code: '', label: '' }];
        setEditingLookup({ ...editingLookup, options: newOptions });
    };

    const handleUpdateOption = (index: number, field: string, value: string) => {
        if (!editingLookup) return;
        const newOptions = [...editingLookup.options];
        newOptions[index] = { ...newOptions[index], [field]: field === 'code' && !isNaN(Number(value)) ? Number(value) : value };
        setEditingLookup({ ...editingLookup, options: newOptions });
    };

    const handleRemoveOption = (index: number) => {
        if (!editingLookup) return;
        const newOptions = editingLookup.options.filter((_, i) => i !== index);
        setEditingLookup({ ...editingLookup, options: newOptions });
    };

    const filteredUsers = users.filter(u => 
        u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort lookups alphabetically by key
    const sortedLookups = Object.entries(currentLookups).sort(([a], [b]) => a.localeCompare(b));

    return (
        <div className="space-y-6 animate-fade-in">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-white">System Administration</h2>
                {tab === 'Users' && (
                    <div className="flex gap-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-500" />
                            <input 
                                type="text" 
                                placeholder="Search users..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="bg-slate-900 border border-slate-800 rounded-xl pl-10 pr-4 py-2 text-sm text-white focus:ring-2 focus:ring-titan-500 outline-none w-64 transition-all"
                            />
                        </div>
                        <button 
                            onClick={() => {
                                setEditingUser({ firstName: '', lastName: '', email: '', role: 'Customer/Basic', status: 'ACTV', password: '' });
                                setShowUserModal(true);
                            }}
                            className="bg-titan-500 hover:bg-titan-600 text-white px-4 py-2 rounded-xl font-bold flex items-center gap-2 transition shadow-lg shadow-titan-500/20"
                        >
                            <Plus className="w-4 h-4" /> Add User
                        </button>
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="flex border-b border-slate-800">
                <button 
                    onClick={() => setTab('Users')}
                    className={`px-6 py-3 font-bold text-sm transition-all border-b-2 flex items-center gap-2 ${tab === 'Users' ? 'border-titan-500 text-titan-500 bg-titan-500/5' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                >
                    <Users className="w-4 h-4" /> Users
                </button>
                <button 
                    onClick={() => setTab('Config')}
                    className={`px-6 py-3 font-bold text-sm transition-all border-b-2 flex items-center gap-2 ${tab === 'Config' ? 'border-titan-500 text-titan-500 bg-titan-500/5' : 'border-transparent text-slate-500 hover:text-slate-300'}`}
                >
                    <Settings className="w-4 h-4" /> Configuration
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center p-12">
                    <Loader2 className="w-8 h-8 text-titan-500 animate-spin" />
                </div>
            ) : error ? (
                <div className="bg-red-900/20 border border-red-900/50 p-4 rounded-xl flex items-center gap-3 text-red-400">
                    <AlertTriangle className="w-5 h-5" />
                    <span>{error}</span>
                </div>
            ) : (
                <>
                    {tab === 'Users' && (
                        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-xl">
                            <table className="w-full text-left">
                                <thead className="bg-slate-950 text-slate-500 text-xs uppercase tracking-wider">
                                    <tr>
                                        <th className="px-6 py-4">Name</th>
                                        <th className="px-6 py-4">Email</th>
                                        <th className="px-6 py-4">Role</th>
                                        <th className="px-6 py-4">Status</th>
                                        <th className="px-6 py-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-800">
                                    {filteredUsers.map(user => (
                                        <tr key={user.id} className="hover:bg-slate-800/50 transition-colors group">
                                            <td className="px-6 py-4 text-white font-medium">
                                                {user.firstName} {user.lastName}
                                            </td>
                                            <td className="px-6 py-4 text-slate-400">
                                                {user.email}
                                            </td>
                                            <td className="px-6 py-4 text-slate-300">
                                                {getCoreLabel('user_role', user.role)}
                                            </td>
                                            <td className="px-6 py-4">
                                                <StatusBadge status={user.status} label={getCoreLabel('user_status', user.status)} />
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <button 
                                                    onClick={() => {
                                                        setEditingUser(user);
                                                        setShowUserModal(true);
                                                    }}
                                                    className="p-2 text-slate-500 hover:text-titan-500 hover:bg-titan-500/10 rounded-lg transition-all"
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {tab === 'Config' && (
                        <div className="grid grid-cols-12 gap-6 min-h-[600px]">
                            {/* Module Sidebar */}
                            <div className="col-span-3 space-y-2">
                                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest px-2 mb-4">Modules</h3>
                                {modules.map(m => (
                                    <button 
                                        key={m.id}
                                        onClick={() => setSelectedModule(m)}
                                        className={`w-full text-left px-4 py-3 rounded-xl transition-all flex items-center justify-between group ${selectedModule?.id === m.id ? 'bg-titan-500 text-white shadow-lg shadow-titan-500/20' : 'bg-slate-900 text-slate-400 border border-slate-800 hover:border-slate-700'}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <Database className={`w-4 h-4 ${selectedModule?.id === m.id ? 'text-white' : 'text-titan-500'}`} />
                                            <span className="text-sm font-bold">{m.name}</span>
                                        </div>
                                        <ChevronRight className={`w-4 h-4 transition-transform ${selectedModule?.id === m.id ? 'translate-x-1' : 'opacity-0 group-hover:opacity-100'}`} />
                                    </button>
                                ))}
                            </div>

                            {/* Lookup List */}
                            <div className="col-span-9 bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-xl">
                                {selectedModule ? (
                                    <div className="space-y-6">
                                        <div className="flex justify-between items-center border-b border-slate-800 pb-4">
                                            <div>
                                                <h3 className="text-xl font-bold text-white">{selectedModule.name} Lookups</h3>
                                                <p className="text-xs text-slate-500 mt-1">Configure dropdown options and status codes for this module.</p>
                                            </div>
                                            {/* Hide New Lookup button as per user request */}
                                            {/* <button 
                                                onClick={() => {
                                                    setEditingLookup({ name: '', options: [] });
                                                    setShowLookupModal(true);
                                                }}
                                                className="bg-slate-800 hover:bg-slate-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-2 border border-slate-700"
                                            >
                                                <Plus className="w-3 h-3" /> New Lookup
                                            </button> */}
                                        </div>

                                        <div className="space-y-4">
                                            {sortedLookups.map(([name, options]) => (
                                                <div key={name} className="bg-slate-950 border border-slate-800 rounded-xl overflow-hidden">
                                                    <div className="bg-slate-900/50 px-4 py-2 border-b border-slate-800 flex justify-between items-center">
                                                        <span className="text-xs font-mono text-titan-500 font-bold uppercase">{name.replace(/_/g, ' ')}</span>
                                                        <button 
                                                            onClick={() => {
                                                                setEditingLookup({ name, options });
                                                                setShowLookupModal(true);
                                                            }}
                                                            className="flex items-center gap-1.5 text-[10px] text-slate-500 hover:text-white transition uppercase font-bold tracking-tighter"
                                                        >
                                                            <Pencil className="w-3 h-3" /> Manage Options
                                                        </button>
                                                    </div>
                                                    <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                                                        {[...options].sort((a: any, b: any) => a.label.localeCompare(b.label)).map((opt: any) => (
                                                            <div key={opt.code} className="bg-slate-900 border border-slate-800 p-2 rounded-lg flex items-center justify-between group">
                                                                <div className="flex flex-col">
                                                                    <span className="text-[10px] text-slate-500 font-mono leading-none mb-1">{opt.code}</span>
                                                                    <span className="text-sm text-slate-200 font-medium leading-none">{opt.label}</span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                            {sortedLookups.length === 0 && (
                                                <div className="text-center py-12">
                                                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-800 mb-4">
                                                        <Database className="w-6 h-6 text-slate-600" />
                                                    </div>
                                                    <p className="text-slate-500 text-sm italic">No lookups defined for this module yet.</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-full text-slate-500 gap-4">
                                        <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center border border-slate-700">
                                            <Database className="w-8 h-8 opacity-20" />
                                        </div>
                                        <p>Select a module to view and manage lookups.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Lookup Modal */}
            {showLookupModal && editingLookup && (
                <Modal 
                    title={Object.keys(currentLookups).includes(editingLookup.name) ? `Edit Lookup: ${editingLookup.name}` : 'Create New Lookup'} 
                    onClose={() => setShowLookupModal(false)}
                    size="md"
                >
                    <form onSubmit={handleSaveLookup} className="space-y-6">
                        {!Object.keys(currentLookups).includes(editingLookup.name) && (
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Lookup Name (Internal Key)</label>
                                <input 
                                    type="text"
                                    value={editingLookup.name}
                                    onChange={(e) => setEditingLookup({ ...editingLookup, name: e.target.value })}
                                    placeholder="e.g. incident_priority"
                                    className="w-full bg-slate-950 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none"
                                    required
                                />
                            </div>
                        )}

                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <label className="block text-xs font-bold text-slate-500 uppercase">Options (Code & Label)</label>
                                <button 
                                    type="button"
                                    onClick={handleAddOption}
                                    className="text-titan-500 hover:text-titan-400 text-xs font-bold flex items-center gap-1"
                                >
                                    <Plus className="w-3 h-3" /> Add Option
                                </button>
                            </div>
                            
                            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
                                {editingLookup.options.map((opt, index) => (
                                    <div key={index} className="flex gap-2 group">
                                        <input 
                                            type="text"
                                            value={opt.code}
                                            disabled
                                            className="w-24 bg-slate-900 border border-slate-800 text-slate-500 p-2 rounded text-sm outline-none cursor-not-allowed"
                                            required
                                        />
                                        <input 
                                            type="text"
                                            value={opt.label}
                                            onChange={(e) => handleUpdateOption(index, 'label', e.target.value)}
                                            placeholder="Display Label"
                                            className="flex-1 bg-slate-950 border border-slate-700 text-white p-2 rounded text-sm focus:border-titan-500 outline-none"
                                            required
                                        />
                                        <button 
                                            type="button"
                                            onClick={() => handleRemoveOption(index)}
                                            className="p-2 text-slate-600 hover:text-red-500 transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                                {editingLookup.options.length === 0 && (
                                    <p className="text-center py-4 text-slate-500 text-sm italic">Add at least one option.</p>
                                )}
                            </div>
                        </div>

                        <div className="pt-4 border-t border-slate-700 flex justify-end gap-3">
                            <button 
                                type="button" 
                                onClick={() => setShowLookupModal(false)}
                                className="px-4 py-2 text-slate-400 hover:text-white transition"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                disabled={isSaving || editingLookup.options.length === 0}
                                className="bg-titan-500 hover:bg-titan-600 disabled:opacity-50 text-white px-6 py-2 rounded-xl font-bold flex items-center gap-2"
                            >
                                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                Save Lookup
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* User Modal */}
            {showUserModal && editingUser && (
                <Modal 
                    title={editingUser.id ? 'Edit User' : 'Add New User'} 
                    onClose={() => setShowUserModal(false)}
                    size="md"
                >
                    <form onSubmit={handleSaveUser} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">First Name</label>
                                <input 
                                    type="text"
                                    value={editingUser.firstName}
                                    onChange={(e) => setEditingUser({ ...editingUser, firstName: e.target.value })}
                                    className="w-full bg-slate-950 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Last Name</label>
                                <input 
                                    type="text"
                                    value={editingUser.lastName}
                                    onChange={(e) => setEditingUser({ ...editingUser, lastName: e.target.value })}
                                    className="w-full bg-slate-950 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Email Address</label>
                            <input 
                                type="email"
                                value={editingUser.email}
                                onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                                className="w-full bg-slate-950 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none"
                                required
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Password {editingUser.id && '(Leave blank to keep current)'}</label>
                            <input 
                                type="password"
                                value={editingUser.password || ''}
                                onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                                className="w-full bg-slate-950 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none"
                                required={!editingUser.id}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Role</label>
                                <select 
                                    value={editingUser.role}
                                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                                    className="w-full bg-slate-950 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none"
                                    required
                                >
                                    {(coreLookups['user_role'] || []).map((r: any) => (
                                        <option key={r.code} value={r.code}>{r.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-2">Status</label>
                                <select 
                                    value={editingUser.status}
                                    onChange={(e) => setEditingUser({ ...editingUser, status: e.target.value })}
                                    className="w-full bg-slate-950 border border-slate-700 text-white p-2 rounded focus:border-titan-500 outline-none"
                                    required
                                >
                                    {(coreLookups['user_status'] || []).map((s: any) => (
                                        <option key={s.code} value={s.code}>{s.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="pt-4 border-t border-slate-700 flex justify-end gap-3">
                            <button 
                                type="button" 
                                onClick={() => setShowUserModal(false)}
                                className="px-4 py-2 text-slate-400 hover:text-white transition"
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit"
                                disabled={isSaving}
                                className="bg-titan-500 hover:bg-titan-600 disabled:opacity-50 text-white px-6 py-2 rounded-xl font-bold flex items-center gap-2"
                            >
                                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                Save User
                            </button>
                        </div>
                    </form>
                </Modal>
            )}
        </div>
    );
};
